import { GraphQLClient } from 'graphql-request';
import { CacheManager } from './CacheManager.js';
import { getProgressIcon } from '../ui/theme.js';
import { AuthenticationError } from '../errors/index.js';
// Import the queries - we'll use them for both string queries and typed SDK
import {
  GET_VIEWER,
  GET_ORGANIZATIONS,
  GET_PIPELINES,
  GET_PIPELINE,
  GET_BUILDS,
  GET_VIEWER_BUILDS,
  GET_BUILD_ANNOTATIONS,
  GET_BUILD_SUMMARY,
  GET_BUILD_FULL,
  GET_BUILD_JOBS_PAGE,
  GET_BUILD_ANNOTATION_TIMESTAMPS,
  GET_BUILD_ANNOTATIONS_FULL
} from '../graphql/queries.js';
// Import generated types
import { 
  GetViewerQuery, 
  GetOrganizationsQuery, 
  GetPipelinesQuery, 
  GetPipelinesQueryVariables,
  GetBuildsQuery,
  GetBuildsQueryVariables,
  GetViewerBuildsQuery,
  GetViewerBuildsQueryVariables,
} from '../graphql/generated/sdk.js';
import { logger } from './logger.js';

// Note: We're now using the automatically generated types from GraphQL Codegen

export interface BuildkiteClientOptions {
  debug?: boolean;
  baseUrl?: string;
  caching?: boolean;
  cacheTTLs?: Partial<{
    viewer: number;
    organizations: number;
    pipelines: number;
    builds: number;
    default: number;
  }>;
}

export interface RateLimitInfo {
  remaining: number;
  limit: number;
  reset: number;
}

/**
 * BuildkiteClient provides methods to interact with the Buildkite GraphQL API
 */
export class BuildkiteClient {
  private client: GraphQLClient;
  private token: string;
  private baseUrl: string = 'https://graphql.buildkite.com/v1';
  private cacheManager: CacheManager | null = null;
  private debug: boolean = false;
  private rateLimitInfo: RateLimitInfo | null = null;
  /**
   * Create a new BuildkiteClient
   * @param token Your Buildkite API token
   * @param options Configuration options
   */
  constructor(token: string, options?: BuildkiteClientOptions, debug?: boolean) {
    this.token = token;
    this.debug = debug || options?.debug || false;
    
    if (this.debug) {
      logger.debug('Initializing BuildkiteClient with options:', {
        baseUrl: options?.baseUrl || this.baseUrl,
        caching: options?.caching !== false,
        debug: this.debug,
        tokenLength: token ? token.length : 0
      });
    }
    
    if (options?.baseUrl) {
      this.baseUrl = options.baseUrl;
    }

    this.client = new GraphQLClient(this.baseUrl, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    // Initialize cache if caching is enabled
    if (options?.caching !== false) {
      if (this.debug) {
        logger.debug('BuildkiteClient constructor - creating CacheManager');
      }
      this.cacheManager = new CacheManager(options?.cacheTTLs, this.debug);
      // Initialize cache and set token hash (async, but we don't wait)
      this.initCache().catch((err) => {
        logger.debug('Cache initialization failed, continuing without cache:', err);
        this.cacheManager = null;
      });
    } else {
      if (this.debug) {
        logger.debug('BuildkiteClient constructor - caching disabled');
      }
    }
  }

  /**
   * Initialize cache asynchronously
   */
  private async initCache(): Promise<void> {
    if (this.cacheManager) {
      await this.cacheManager.init();
      await this.cacheManager.setTokenHash(this.token);
    }
  }

  /**
   * Execute a GraphQL query
   * @param query The GraphQL query
   * @param variables Variables for the query
   * @returns The query response
   */
  public async query<T = unknown, V extends Record<string, any> = Record<string, any>>(
    query: string,
    variables?: V
  ): Promise<T> {
    try {
      const startTime = process.hrtime.bigint();
      const operationName = query.match(/query\s+(\w+)?/)?.[1] || 'UnnamedQuery';
      if (this.debug) {
        logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: ${operationName}`);
      }
      
      // Check if result is in cache
      if (this.cacheManager) {
              if (this.debug) {
        logger.debug('query() - cacheManager exists, checking cache');
      }
        const cachedResult = await this.cacheManager.get<T>(query, variables);
        
        if (cachedResult) {
          if (this.debug) {
            logger.debug(`${getProgressIcon('SUCCESS_LOG')} Served from cache: ${operationName}`);
          }
          return cachedResult;
        }
      }
      
      const response = await this.client.request<T>(query, variables);

      // Store result in cache if caching is enabled
      if (this.cacheManager) {
        await this.cacheManager.set(query, response, variables);
      }
      
      const endTime = process.hrtime.bigint();
      const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
      if (this.debug) {
        logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL query completed: ${operationName} (${duration.toFixed(2)}ms)`);
      }
      
      return response;
    } catch (error: unknown) {
      const isAuthError = this.isAuthenticationError(error as any);
      if (isAuthError && this.debug) {
        logger.debug('Authentication error detected, not caching result');
      }

      if (this.debug) {
        logger.error(error as any, 'Error in GraphQL query');
        // Log raw error information
        logger.debug('Raw error object:', {
          error,
          type: typeof error,
          constructor: (error as any)?.constructor?.name,
          keys: error && typeof error === 'object' ? Object.keys(error as any) : undefined
        });
        // Log more detailed error information
        if (error instanceof Error && 'response' in (error as any)) {
          const response = (error as any).response;
          logger.debug('GraphQL error details:', {
            status: response?.status,
            statusText: response?.statusText,
            errors: response?.errors,
            data: response?.data,
            headers: response?.headers ? Object.fromEntries(response.headers.entries()) : undefined
          });
        }
      }

      // Convert authentication errors to user-friendly AuthenticationError
      if (isAuthError) {
        throw AuthenticationError.fromGraphQLError(error as Error);
      }

      throw error;
    }
  }

  /**
   * Check if an error is an authentication error
   */
  private isAuthenticationError(error: any): boolean {
    // Check for common authentication error patterns
    if (error.response?.errors) {
      const errors = error.response.errors;
      return errors.some((err: any) =>
        err.message?.includes('unauthorized') ||
        err.message?.includes('authentication') ||
        err.message?.includes('permission') ||
        err.message?.includes('invalid token')
      );
    }

    // Check for HTTP status codes that indicate auth issues
    if (error.response?.status) {
      const status = error.response.status;
      return status === 401 || status === 403;
    }

    // Check error message directly
    if (error.message) {
      return error.message.includes('unauthorized') ||
             error.message.includes('authentication') ||
             error.message.includes('permission') ||
             error.message.includes('invalid token');
    }

    return false;
  }

  /**
   * Execute a GraphQL request with authentication error handling.
   * Wraps this.client.request() to convert auth errors to AuthenticationError.
   */
  private async request<T>(query: string, variables?: Record<string, any>): Promise<T> {
    try {
      return await this.client.request<T>(query, variables);
    } catch (error) {
      if (this.isAuthenticationError(error)) {
        throw AuthenticationError.fromGraphQLError(error as Error);
      }
      throw error;
    }
  }

  /**
   * Execute a GraphQL mutation
   * @param mutation The GraphQL mutation
   * @param variables Variables for the mutation
   * @returns The mutation response
   */
  public async mutate<T = unknown, V extends Record<string, any> = Record<string, any>>(
    mutation: string,
    variables?: V
  ): Promise<T> {
    try {
      const startTime = process.hrtime.bigint();
      const operationName = mutation.match(/mutation\s+(\w+)?/)?.[1] || 'UnnamedMutation';
      if (this.debug) {
        logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL mutation: ${operationName}`);
      }

      const result = await this.request<T>(mutation, variables);
      
      // Invalidate relevant caches after mutations
      if (this.cacheManager) {
        // Determine what cache types to invalidate based on mutation name/content
        if (mutation.includes('Pipeline')) {
          await this.cacheManager.invalidateType('pipelines');
        } else if (mutation.includes('Build')) {
          await this.cacheManager.invalidateType('builds');
        }
      }
      
      const endTime = process.hrtime.bigint();
      const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
      logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL mutation completed: ${operationName} (${duration.toFixed(2)}ms)`);
      
      return result;
    } catch (error) {
      logger.error('GraphQL mutation error:', error);
      throw error;
    }
  }

  /**
   * Get the organization slugs for the current viewer
   * @returns An array of organization slugs the current user belongs to
   */
  public async getViewerOrganizationSlugs(): Promise<string[]> {
    try {
      const startTime = process.hrtime.bigint();
      if (this.debug) {
        logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: getViewerOrganizationSlugs`);
      }
      
      // Get the organizations using our query
      if (this.debug) {
        logger.debug('About to call this.query with GET_ORGANIZATIONS');
      }
      const data = await this.query<GetOrganizationsQuery>(GET_ORGANIZATIONS.toString());
      if (this.debug) {
        logger.debug('Successfully got data from this.query');
      }
      
      if (this.debug) {
        logger.debug('Raw GraphQL response for organizations:', {
          hasData: !!data,
          hasViewer: !!data?.viewer,
          hasOrganizations: !!data?.viewer?.organizations,
          hasEdges: !!data?.viewer?.organizations?.edges,
          edgesLength: data?.viewer?.organizations?.edges?.length || 0
        });
      }
      
      // Use our helper method to process the response
      const organizations = this.processOrganizationsResponse(data);
      
      if (this.debug) {
        logger.debug('Processed organizations:', {
          count: organizations.length,
          organizations: organizations.map(org => ({ id: org.id, name: org.name, slug: org.slug }))
        });
      }
      
      if (organizations.length === 0) {
        if (this.debug) {
          logger.debug('No organizations found in response', { data });
        }
        return []
      }
      
      // Map to just the slugs
      const slugs = organizations.map(org => org.slug);
      
      const endTime = process.hrtime.bigint();
      const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
      if (this.debug) {
        logger.debug(`${getProgressIcon('SUCCESS_LOG')} Found ${slugs.length} organizations (${duration.toFixed(2)}ms)`);
      }
      
      return slugs;
    } catch (error) {
      if (this.debug) {
        logger.debug('GraphQL query failed', {
          error: error instanceof Error ? error.message : String(error),
          details: error instanceof Error ? error : undefined
        });
        
        // Log more detailed error information
        if (error instanceof Error && 'response' in error) {
          const response = (error as any).response;
          logger.debug('GraphQL error response:', {
            status: response?.status,
            statusText: response?.statusText,
            errors: response?.errors,
            data: response?.data
          });
        }
        
        // Log detailed error information
        logger.debug('Error in getViewerOrganizationSlugs:', {
          errorMessage: error instanceof Error ? error.message : String(error),
          errorType: error?.constructor?.name,
          hasResponse: error instanceof Error && 'response' in error,
          response: error instanceof Error && 'response' in error ? (error as any).response : undefined
        });
      }
      throw new Error('Failed to determine your organizations', { cause: error });
    }
  }

  /**
   * Clear all cache entries
   */
  public async clearCache(): Promise<void> {
    if (this.cacheManager) {
      await this.cacheManager.clear();
    }
  }

  /**
   * Invalidate a specific cache type
   */
  public async invalidateCache(type: string): Promise<void> {
    if (this.cacheManager) {
      await this.cacheManager.invalidateType(type);
    }
  }

  /**
   * Get the current viewer information with type safety
   * @returns The viewer data
   */
  public async getViewer(): Promise<GetViewerQuery> {
    if (this.debug) {
      logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: GetViewer`);
    }

    // Check if result is in cache
    if (this.cacheManager) {
      const cachedResult = await this.cacheManager.get<GetViewerQuery>(GET_VIEWER.toString(), {});
      if (cachedResult) {
        if (this.debug) {
          logger.debug(`${getProgressIcon('SUCCESS_LOG')} Served from cache: GetViewer`);
        }
        return cachedResult;
      }
    }

    const startTime = process.hrtime.bigint();
    const result = await this.request<GetViewerQuery>(GET_VIEWER.toString());

    // Store result in cache if caching is enabled
    if (this.cacheManager) {
      await this.cacheManager.set(GET_VIEWER.toString(), result, {});
    }

    const endTime = process.hrtime.bigint();
    const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
    if (this.debug) {
      logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL query completed: GetViewer (${duration.toFixed(2)}ms)`);
    }

    return result;
  }

  /**
   * Get organizations for the current viewer
   * @returns An array of organization objects with id, name, and slug
   */
  public async getOrganizations(): Promise<Array<{ id: string; name: string; slug: string; }>> {
    if (this.debug) {
      logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: GetOrganizations`);
    }

    // Check if result is in cache
    if (this.cacheManager) {
      const cachedResult = await this.cacheManager.get<GetOrganizationsQuery>(GET_ORGANIZATIONS.toString(), {});
      if (cachedResult) {
        if (this.debug) {
          logger.debug(`${getProgressIcon('SUCCESS_LOG')} Served from cache: GetOrganizations`);
        }
        return this.processOrganizationsResponse(cachedResult);
      }
    }

    const startTime = process.hrtime.bigint();
    const result = await this.request<GetOrganizationsQuery>(GET_ORGANIZATIONS.toString());

    // Store result in cache if caching is enabled
    if (this.cacheManager) {
      await this.cacheManager.set(GET_ORGANIZATIONS.toString(), result, {});
    }

    const endTime = process.hrtime.bigint();
    const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
    if (this.debug) {
      logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL query completed: GetOrganizations (${duration.toFixed(2)}ms)`);
    }

    return this.processOrganizationsResponse(result);
  }

  /**
   * Process the raw GraphQL organizations response into a clean array
   * @param data The raw GraphQL response
   * @returns A processed array of organization objects
   * @private
   */
  private processOrganizationsResponse(data: GetOrganizationsQuery): Array<{ id: string; name: string; slug: string; }> {
    if (!data?.viewer?.organizations?.edges) {
      return [];
    }
    
    const edges = data.viewer.organizations.edges;
    
    // Filter out null edges and map to non-null nodes
    const organizations = edges
      .filter((edge): edge is NonNullable<typeof edge> => edge !== null)
      .map(edge => edge.node)
      .filter((node): node is NonNullable<typeof node> => node !== null);
      
    return organizations;
  }

  /**
   * Get pipelines for an organization with type safety
   * @param organizationSlug The organization slug
   * @param first Number of pipelines to retrieve
   * @param after Cursor for pagination
   * @returns The pipelines data
   */
  public async getPipelines(
    organizationSlug: string, 
    first?: number, 
    after?: string
  ): Promise<GetPipelinesQuery> {
    const variables: GetPipelinesQueryVariables = {
      organizationSlug,
      first,
      after,
    };

    if (this.debug) {
      logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: GetPipelines for ${organizationSlug}`);
    }

    // Check if result is in cache
    if (this.cacheManager) {
      const cachedResult = await this.cacheManager.get<GetPipelinesQuery>(GET_PIPELINES.toString(), variables);
      if (cachedResult) {
        if (this.debug) {
          logger.debug(`${getProgressIcon('SUCCESS_LOG')} Served from cache: GetPipelines`);
        }
        return cachedResult;
      }
    }

    const startTime = process.hrtime.bigint();
    const result = await this.request<GetPipelinesQuery>(GET_PIPELINES.toString(), variables);

    // Store result in cache if caching is enabled
    if (this.cacheManager) {
      await this.cacheManager.set(GET_PIPELINES.toString(), result, variables);
    }

    const endTime = process.hrtime.bigint();
    const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
    if (this.debug) {
      logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL query completed: GetPipelines (${duration.toFixed(2)}ms)`);
    }

    return result;
  }

  /**
   * Get a single pipeline by organization and pipeline slug
   * Prefers exact slug match over partial/fuzzy matches
   */
  public async getPipeline(
    orgSlug: string,
    pipelineSlug: string
  ): Promise<any> {
    const variables = {
      organizationSlug: orgSlug,
      pipelineSlug: pipelineSlug,
    };

    if (this.debug) {
      logger.debug('Fetching pipeline:', variables);
    }

    const data = await this.query<any>(GET_PIPELINE.toString(), variables);

    const pipelines = data.organization?.pipelines?.edges || [];

    if (this.debug) {
      logger.debug('Pipeline search results:', {
        count: pipelines.length,
        slugs: pipelines.map((e: any) => e.node?.slug),
      });
    }

    // Prefer exact slug match over partial match
    const exactMatch = pipelines.find((edge: any) => edge.node?.slug === pipelineSlug);
    const result = exactMatch?.node || pipelines[0]?.node;

    if (this.debug && exactMatch) {
      logger.debug('Found exact slug match:', exactMatch.node?.slug);
    }

    return result;
  }

  /**
   * Get builds for a pipeline with type safety. Resolves the pipeline by its
   * exact "org/slug" via the top-level pipeline(slug:) field, so a fuzzy
   * substring can no longer match a sibling pipeline.
   * @param pipelineSlug The pipeline slug
   * @param organizationSlug The organization slug
   * @param first Number of builds to retrieve
   * @returns The builds data
   */
  public async getBuilds(
    pipelineSlug: string,
    organizationSlug: string,
    options: {
      first?: number;
      createdAtFrom?: string;
      createdAtTo?: string;
      state?: string[];
      branch?: string[];
    } = {}
  ): Promise<GetBuildsQuery> {
    // The top-level pipeline(slug:) field takes a single combined "org/slug".
    const combinedSlug = `${organizationSlug}/${pipelineSlug}`;
    const variables: GetBuildsQueryVariables = {
      pipelineSlug: combinedSlug,
      first: options.first,
      createdAtFrom: options.createdAtFrom,
      createdAtTo: options.createdAtTo,
      state: options.state as any,
      branch: options.branch,
    };

    if (this.debug) {
      logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: GetBuilds for ${pipelineSlug} in ${organizationSlug}`);
    }

    // Check if result is in cache
    if (this.cacheManager) {
      const cachedResult = await this.cacheManager.get<GetBuildsQuery>(GET_BUILDS.toString(), variables);
      if (cachedResult) {
        if (this.debug) {
          logger.debug(`${getProgressIcon('SUCCESS_LOG')} Served from cache: GetBuilds`);
        }
        return cachedResult;
      }
    }

    const startTime = process.hrtime.bigint();
    const result = await this.request<GetBuildsQuery>(GET_BUILDS.toString(), variables);

    // Store result in cache if caching is enabled
    if (this.cacheManager) {
      await this.cacheManager.set(GET_BUILDS.toString(), result, variables);
    }

    const endTime = process.hrtime.bigint();
    const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
    if (this.debug) {
      logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL query completed: GetBuilds (${duration.toFixed(2)}ms)`);
    }

    return result;
  }

  /**
   * Get builds for the current viewer with type safety
   * @param first Number of builds to retrieve
   * @returns The viewer builds data
   */
  public async getViewerBuilds(first: number): Promise<GetViewerBuildsQuery> {
    const variables: GetViewerBuildsQueryVariables = {
      first,
    };

    if (this.debug) {
      logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: GetViewerBuilds`);
    }

    // Check if result is in cache
    if (this.cacheManager) {
      const cachedResult = await this.cacheManager.get<GetViewerBuildsQuery>(GET_VIEWER_BUILDS.toString(), variables);
      if (cachedResult) {
        if (this.debug) {
          logger.debug(`${getProgressIcon('SUCCESS_LOG')} Served from cache: GetViewerBuilds`);
        }
        return cachedResult;
      }
    }

    const startTime = process.hrtime.bigint();
    const result = await this.request<GetViewerBuildsQuery>(GET_VIEWER_BUILDS.toString(), variables);

    // Store result in cache if caching is enabled
    if (this.cacheManager) {
      await this.cacheManager.set(GET_VIEWER_BUILDS.toString(), result, variables);
    }

    const endTime = process.hrtime.bigint();
    const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
    if (this.debug) {
      logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL query completed: GetViewerBuilds (${duration.toFixed(2)}ms)`);
    }

    return result;
  }

  /**
   * Get the current rate limit information
   * @returns Current rate limit information or null if not available
   */
  public getRateLimitInfo(): RateLimitInfo | null {
    return this.rateLimitInfo;
  }

  /**
   * Get annotations for a build with type safety
   * @param buildSlug The build slug (e.g., "org/pipeline/number")
   * @returns The build annotations data
   */
  public async getBuildAnnotations(buildSlug: string): Promise<any> {
    const variables = {
      buildSlug,
    };

    if (this.debug) {
      logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: GetBuildAnnotations for ${buildSlug}`);
    }

    // Check if result is in cache
    if (this.cacheManager) {
      const cachedResult = await this.cacheManager.get<any>(GET_BUILD_ANNOTATIONS.toString(), variables);
      if (cachedResult) {
        if (this.debug) {
          logger.debug(`${getProgressIcon('SUCCESS_LOG')} Served from cache: GetBuildAnnotations`);
        }
        return cachedResult;
      }
    }

    const startTime = process.hrtime.bigint();
    const result = await this.request<any>(GET_BUILD_ANNOTATIONS.toString(), variables);

    // Store result in cache if caching is enabled
    if (this.cacheManager) {
      await this.cacheManager.set(GET_BUILD_ANNOTATIONS.toString(), result, variables);
    }

    const endTime = process.hrtime.bigint();
    const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
    if (this.debug) {
      logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL query completed: GetBuildAnnotations (${duration.toFixed(2)}ms)`);
    }

    return result;
  }

  public async getBuildSummary(buildSlug: string): Promise<any> {
    const variables = {
      slug: buildSlug,
    };

    if (this.debug) {
      logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: GetBuildSummary for ${buildSlug}`);
    }

    // Check if result is in cache
    if (this.cacheManager) {
      const cachedResult = await this.cacheManager.get<any>('GET_BUILD_SUMMARY', variables);
      if (cachedResult) {
        if (this.debug) {
          logger.debug(`${getProgressIcon('SUCCESS_LOG')} Served from cache: GetBuildSummary`);
        }
        return cachedResult;
      }
    }

    const startTime = process.hrtime.bigint();
    const result = await this.request<any>(GET_BUILD_SUMMARY.toString(), variables);

    if (!result.build) {
      throw new Error(`Build not found: ${buildSlug}`);
    }

    // Store result in cache if caching is enabled
    if (this.cacheManager) {
      await this.cacheManager.set('GET_BUILD_SUMMARY', result, variables);
    }

    const endTime = process.hrtime.bigint();
    const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
    if (this.debug) {
      logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL query completed: GetBuildSummary (${duration.toFixed(2)}ms)`);
    }

    return result;
  }

  public async getBuildFull(buildSlug: string): Promise<any> {
    const variables = {
      slug: buildSlug,
    };

    if (this.debug) {
      logger.debug(`${getProgressIcon('STARTING')} Starting GraphQL query: GetBuildFull for ${buildSlug}`);
    }

    // Check if result is in cache
    if (this.cacheManager) {
      const cachedResult = await this.cacheManager.get<any>('GET_BUILD_FULL', variables);
      if (cachedResult) {
        if (this.debug) {
          logger.debug(`${getProgressIcon('SUCCESS_LOG')} Served from cache: GetBuildFull`);
        }
        return cachedResult;
      }
    }

    const startTime = process.hrtime.bigint();
    const result = await this.request<any>(GET_BUILD_FULL.toString(), variables);

    if (!result.build) {
      throw new Error(`Build not found: ${buildSlug}`);
    }

    // Store result in cache if caching is enabled
    if (this.cacheManager) {
      await this.cacheManager.set('GET_BUILD_FULL', result, variables);
    }

    const endTime = process.hrtime.bigint();
    const duration = Number(endTime - startTime) / 1000000; // Convert to milliseconds
    if (this.debug) {
      logger.debug(`${getProgressIcon('SUCCESS_LOG')} GraphQL query completed: GetBuildFull (${duration.toFixed(2)}ms)`);
    }

    return result;
  }

  /**
   * Fetch remaining job pages for a build
   * Uses the initial data from GET_BUILD_SUMMARY and only fetches additional pages
   * @param buildSlug The build slug
   * @param initialJobs Jobs already fetched from GET_BUILD_SUMMARY
   * @param initialPageInfo PageInfo from GET_BUILD_SUMMARY
   * @param options Pagination options
   * @returns Complete job list including initial and additional jobs
   */
  public async fetchRemainingJobs(
    buildSlug: string,
    initialJobs: any[],
    initialPageInfo: { hasNextPage: boolean; endCursor: string | null },
    options?: {
      onProgress?: (fetched: number, total?: number) => void;
    }
  ): Promise<{ jobs: any[], totalCount: number }> {
    // Start with the jobs we already have
    const allJobs = [...initialJobs];
    let cursor = initialPageInfo.endCursor;
    let hasMore = initialPageInfo.hasNextPage;
    
    if (!hasMore) {
      // No additional pages needed
      return { jobs: allJobs, totalCount: allJobs.length };
    }
    
    if (this.debug) {
      logger.debug(`Build has additional job pages, starting pagination from cursor: ${cursor?.substring(0, 20)}...`);
    }
    
    while (hasMore && cursor) {
      const variables = {
        slug: buildSlug,
        first: 100,
        after: cursor
      };
      
      if (this.debug) {
        logger.debug(`Fetching additional jobs page: cursor=${cursor?.substring(0, 20)}...`);
      }
      
      // Use the lightweight page query
      const result = await this.query<any>(
        GET_BUILD_JOBS_PAGE.toString(),
        variables
      );
      
      const jobEdges = result.build?.jobs?.edges || [];
      allJobs.push(...jobEdges);
      
      // Update pagination info
      cursor = result.build?.jobs?.pageInfo?.endCursor || null;
      hasMore = result.build?.jobs?.pageInfo?.hasNextPage || false;
      
      // Report progress if callback provided
      if (options?.onProgress) {
        const totalCount = result.build?.jobs?.count || allJobs.length;
        options.onProgress(allJobs.length, totalCount);
      }
      
      if (this.debug) {
        logger.debug(`Fetched ${jobEdges.length} additional jobs, total: ${allJobs.length}`);
      }
    }
    
    return { jobs: allJobs, totalCount: allJobs.length };
  }

  /**
   * Enhanced getBuildSummary that supports complete job fetching
   */
  public async getBuildSummaryWithAllJobs(
    buildSlug: string,
    options?: {
      fetchAllJobs?: boolean;
      onProgress?: (fetched: number, total?: number) => void;
    }
  ): Promise<any> {
    // Fetch initial build data with first 100 jobs
    const buildData = await this.getBuildSummary(buildSlug);
    
    // Check if we need to fetch additional job pages
    const jobsData = buildData.build?.jobs;
    const pageInfo = jobsData?.pageInfo;
    
    if (!options?.fetchAllJobs || !pageInfo?.hasNextPage) {
      // Either we don't want all jobs, or there are no more pages
      return buildData;
    }
    
    // Fetch remaining job pages
    const { jobs: allJobs, totalCount } = await this.fetchRemainingJobs(
      buildSlug,
      jobsData.edges,
      pageInfo,
      { onProgress: options.onProgress }
    );
    
    // Merge the complete job list into the build data
    return {
      ...buildData,
      build: {
        ...buildData.build,
        jobs: {
          edges: allJobs,
          pageInfo: { hasNextPage: false, endCursor: null },
          count: totalCount
        }
      }
    };
  }

  /**
   * Find pipelines matching any of the given repo URL candidates,
   * with the latest build for the specified branch.
   * Uses GraphQL aliases to try all URL formats in a single request.
   */
  public async getPipelineBuildsForRepo(
    orgSlug: string,
    repoCandidates: string[],
    branch: string
  ): Promise<Array<{ id: string; name: string; slug: string; repository: { url: string }; build: any | null }>> {
    const aliases = repoCandidates.map((url, i) => {
      const alias = `repo${i}`;
      return `${alias}: pipelines(first: 50, repository: { url: ${JSON.stringify(url)} }, archived: false) {
        edges {
          node {
            id
            name
            slug
            repository { url }
            builds(first: 1, branch: [${JSON.stringify(branch)}]) {
              edges {
                node {
                  number
                  state
                  url
                  message
                  branch
                  commit
                  createdAt
                  startedAt
                  finishedAt
                }
              }
            }
          }
        }
      }`;
    });

    const query = `query GetPipelineBuildsForRepo($orgSlug: ID!) {
      organization(slug: $orgSlug) {
        ${aliases.join('\n        ')}
      }
    }`;

    const data = await this.query<any>(query, { orgSlug });

    const seen = new Set<string>();
    const results: Array<{ id: string; name: string; slug: string; repository: { url: string }; build: any | null }> = [];

    for (let i = 0; i < repoCandidates.length; i++) {
      const alias = `repo${i}`;
      const edges = data.organization?.[alias]?.edges || [];

      for (const edge of edges) {
        const node = edge.node;
        if (seen.has(node.id)) continue;
        seen.add(node.id);

        const buildEdge = node.builds?.edges?.[0];
        results.push({
          id: node.id,
          name: node.name,
          slug: node.slug,
          repository: node.repository,
          build: buildEdge?.node || null,
        });
      }
    }

    return results;
  }

  /**
   * Find pipelines matching any of the given repo URL candidates.
   * Like getPipelineBuildsForRepo but doesn't require an existing build —
   * used by `bktide build create` to resolve which pipeline to trigger.
   */
  public async getPipelinesForRepo(
    orgSlug: string,
    repoCandidates: string[],
  ): Promise<Array<{ id: string; name: string; slug: string; repository: { url: string } }>> {
    const aliases = repoCandidates.map((url, i) => {
      const alias = `repo${i}`;
      return `${alias}: pipelines(first: 50, repository: { url: ${JSON.stringify(url)} }, archived: false) {
        edges {
          node {
            id
            name
            slug
            repository { url }
          }
        }
      }`;
    });

    const query = `query GetPipelinesForRepo($orgSlug: ID!) {
      organization(slug: $orgSlug) {
        ${aliases.join('\n        ')}
      }
    }`;

    const data = await this.query<any>(query, { orgSlug });

    const seen = new Set<string>();
    const results: Array<{ id: string; name: string; slug: string; repository: { url: string } }> = [];

    for (let i = 0; i < repoCandidates.length; i++) {
      const edges = data.organization?.[`repo${i}`]?.edges || [];
      for (const edge of edges) {
        const node = edge.node;
        if (seen.has(node.id)) continue;
        seen.add(node.id);
        results.push({
          id: node.id,
          name: node.name,
          slug: node.slug,
          repository: node.repository,
        });
      }
    }

    return results;
  }

  /**
   * Get annotation timestamps for change detection (lightweight)
   */
  public async getAnnotationTimestamps(buildSlug: string): Promise<Array<{ uuid: string; updatedAt: string | null; createdAt: string }>> {
    const variables = { slug: buildSlug };
    const data = await this.query<any>(GET_BUILD_ANNOTATION_TIMESTAMPS.toString(), variables);
    return (data.build?.annotations?.edges || []).map((edge: any) => edge.node);
  }

  /**
   * Get full annotation data
   */
  public async getAnnotationsFull(buildSlug: string): Promise<any[]> {
    const variables = { slug: buildSlug };
    const data = await this.query<any>(GET_BUILD_ANNOTATIONS_FULL.toString(), variables);
    return (data.build?.annotations?.edges || []).map((edge: any) => edge.node);
  }
}